"use client"

import { useSyncExternalStore, useCallback } from "react"

export type UserTier = "anonymous" | "free" | "starter" | "team" | "agency" | "enterprise"

export const DEV_MODE = process.env.NEXT_PUBLIC_VERCEL_ENV !== "production"
// </CHANGE>

export const SECTION_ACCESS: Record<UserTier, string[]> = {
  anonymous: ["identity"],
  free: ["identity", "offerings"],
  starter: ["identity", "offerings", "audience", "voice"],
  team: ["identity", "offerings", "audience", "voice", "market", "operations"],
  agency: ["identity", "offerings", "audience", "voice", "market", "operations", "goals", "content"],
  enterprise: ["identity", "offerings", "audience", "voice", "market", "operations", "goals", "content", "advanced"],
}

export interface UserState {
  id: string | null
  email: string | null
  name: string | null
  tier: UserTier
  tenantId: string | null
  isAuthenticated: boolean
  onboardingComplete: boolean
}

const defaultState: UserState = {
  id: null,
  email: null,
  name: null,
  tier: "anonymous",
  tenantId: null,
  isAuthenticated: false,
  onboardingComplete: false,
}

let state: UserState = { ...defaultState }
const listeners = new Set<() => void>()

function emitChange() {
  listeners.forEach((listener) => listener())
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function getSnapshot() {
  return state
}

function getServerSnapshot(): UserState {
  return defaultState
}

// Actions
function setUser(user: Partial<UserState>) {
  state = { ...state, ...user, isAuthenticated: true }
  emitChange()
}

function setTier(tier: UserTier) {
  state = { ...state, tier }
  emitChange()
}

function setTenantId(tenantId: string) {
  state = { ...state, tenantId }
  emitChange()
}

function setOnboardingComplete(complete: boolean) {
  state = { ...state, onboardingComplete: complete }
  emitChange()
}

function logout() {
  state = { ...defaultState }
  emitChange()
}

export function useUserStore() {
  const currentState = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)

  return {
    ...currentState,
    setUser: useCallback(setUser, []),
    setTier: useCallback(setTier, []),
    setTenantId: useCallback(setTenantId, []),
    setOnboardingComplete: useCallback(setOnboardingComplete, []),
    logout: useCallback(logout, []),
  }
}
